import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  setStatus,
} from '../../store/advertisePackage/advertisePackageSlice';
import {
  findAdvertisePackage,
  createAdvertisePackage,
  patchAdvertisePackage,
} from '../../store/advertisePackage/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import Scrollbar from '../../components/Scrollbar';
import AdvertisePackageFormModal from './AdvertisePackageModal';
import {
  IInitialState,
  AdvertisePackage,
} from '../../store/advertisePackage/api/interface';
import Searchbar from '../../components/Searchbar';
import {
  reset as resetAdvertisePackage,
  setStatus as setAdvertisePackageStatus,
} from '../../store/advertisePackage/advertisePackageSlice';
import advertisePackageSchema from '../../validation/advertisePackageSchema';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.advertisePackage);
  const { toggle, onOpen, onClose } = useToggle();
  const [search, setSearch] = useState<string>('');
  const [userType, setUserType] = useState<string>('all');

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findAdvertisePackage({
          limit,
          skip,
          ...(search && { search: search }),
          ...(userType !== 'all' && { type: userType }),
        })
      );
    }
  }, [dispatch, status, limit, skip, search, userType]);

  useEffect(() => {
    dispatch(resetAdvertisePackage());
  }, [search, userType]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik<IInitialState>({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      duration: '',
      price: '',
      code: '',
      type: '',
      activeState: true,
      packageType: '',
      creditCount: '',
      creditExpirationDays: '',
      companyId: null,
      companyIds: [],
      companySelection: '',
      brokerId: null,
      brokerIds: [],
      brokerSelection: '',
    },
    validationSchema: advertisePackageSchema,
    onSubmit: async (values) => {
      try {
        // Prepare request body
        const reqBody: any = {
          labelEn: values.labelEn,
          labelAr: values.labelAr,
          code: values.code,
          price: values.price,
          type: values.type,
          activeState: values.activeState,
        };

        // Add fields based on type
        if (values.type === 'individual') {
          reqBody.packageType = 'duration_based';
          reqBody.duration = values.duration;
          // Clear credit fields for individual packages
          reqBody.creditCount = null;
          reqBody.creditExpirationDays = null;
          reqBody.companyId = null;
          reqBody.companyIds = null;
          reqBody.brokerId = null;
          reqBody.brokerIds = null;
        } else if (values.type === 'company') {
          reqBody.packageType = 'credit_based';
          reqBody.creditCount = parseInt(values.creditCount || '0', 10);
          reqBody.creditExpirationDays = parseInt(
            values.creditExpirationDays || '0',
            10
          );
          reqBody.duration = values.duration; // Ad publishing duration
          
          // Handle company selection
          if (values.companySelection === 'all') {
            // All companies - both fields are null
            reqBody.companyId = null;
            reqBody.companyIds = null;
          } else {
            // Specific companies - store in companyIds array
            const companyIds = values.companyIds || [];
            if (companyIds.length === 0) {
              throw new Error('At least one company must be selected');
            }
            
            if (companyIds.length === 1) {
              // Single company - use companyId for backward compatibility
              reqBody.companyId = companyIds[0];
              reqBody.companyIds = null;
            } else {
              // Multiple companies - store in companyIds array
              reqBody.companyId = null;
              reqBody.companyIds = companyIds;
            }
          }
          // Clear broker fields for company packages
          reqBody.brokerId = null;
          reqBody.brokerIds = null;
        } else if (values.type === 'broker') {
          reqBody.packageType = 'duration_based';
          reqBody.duration = values.duration;
          // Clear credit fields for broker packages
          reqBody.creditCount = null;
          reqBody.creditExpirationDays = null;
          reqBody.companyId = null;
          reqBody.companyIds = null;
          
          // Handle broker selection
          if (values.brokerSelection === 'all') {
            // All brokers - both fields are null
            reqBody.brokerId = null;
            reqBody.brokerIds = null;
          } else {
            // Specific brokers - store in brokerIds array
            const brokerIds = values.brokerIds || [];
            if (brokerIds.length === 0) {
              throw new Error('At least one broker must be selected');
            }
            
            if (brokerIds.length === 1) {
              // Single broker - use brokerId for backward compatibility
              reqBody.brokerId = brokerIds[0];
              reqBody.brokerIds = null;
            } else {
              // Multiple brokers - store in brokerIds array
              reqBody.brokerId = null;
              reqBody.brokerIds = brokerIds;
            }
          }
        } else if (values.type === 'both') {
          // For 'both' type, handle based on packageType
          if (values.packageType === 'duration_based') {
            reqBody.duration = values.duration;
            reqBody.creditCount = null;
            reqBody.creditExpirationDays = null;
          } else if (values.packageType === 'credit_based') {
            reqBody.creditCount = parseInt(values.creditCount || '0', 10);
            reqBody.creditExpirationDays = parseInt(
              values.creditExpirationDays || '0',
              10
            );
            reqBody.duration = values.duration;
          }
          // Clear both company and broker specific fields for 'both' type
          reqBody.companyId = null;
          reqBody.companyIds = null;
          reqBody.brokerId = null;
          reqBody.brokerIds = null;
        } else if (values.type === 'individual_broker') {
          reqBody.packageType = 'duration_based';
          reqBody.duration = values.duration;
          // Clear credit fields for individual_broker packages
          reqBody.creditCount = null;
          reqBody.creditExpirationDays = null;
          // Clear company fields for individual_broker packages
          reqBody.companyId = null;
          reqBody.companyIds = null;
          // Individual + Broker packages are available to ALL individuals and ALL brokers
          // No specific broker selection allowed
          reqBody.brokerId = null;
          reqBody.brokerIds = null;
        }

        // Handle individual packages
        if (values.id) {
          reqBody.id = values.id;
          await dispatch(patchAdvertisePackage(reqBody)).unwrap();
        } else {
          await dispatch(createAdvertisePackage(reqBody)).unwrap();
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: AdvertisePackage): void => {
    onOpen();
    // Determine company selection based on companyId and companyIds
    let companySelection = 'all';
    let companyIds: string[] = [];
    
    if (value.companyIds && Array.isArray(value.companyIds) && value.companyIds.length > 0) {
      // Multiple companies stored in companyIds array
      companySelection = 'specific';
      companyIds = value.companyIds;
    } else if (value.companyId) {
      // Single company stored in companyId (backward compatibility)
      companySelection = 'specific';
      companyIds = [value.companyId];
    }

    // Determine broker selection based on brokerId and brokerIds
    // Note: For 'individual_broker' type, broker selection should always be empty/null
    let brokerSelection = '';
    let brokerIds: string[] = [];
    
    if (value.type === 'individual_broker') {
      // Individual + Broker packages are available to ALL - no specific broker selection
      brokerSelection = '';
      brokerIds = [];
    } else if (value.brokerIds && Array.isArray(value.brokerIds) && value.brokerIds.length > 0) {
      // Multiple brokers stored in brokerIds array
      brokerSelection = 'specific';
      brokerIds = value.brokerIds;
    } else if (value.brokerId) {
      // Single broker stored in brokerId (backward compatibility)
      brokerSelection = 'specific';
      brokerIds = [value.brokerId];
    } else {
      // Default to 'all' for broker type packages
      brokerSelection = 'all';
    }

    setValues({
      id: value.id,
      labelEn: value?.labelEn ?? '',
      labelAr: value?.labelAr ?? '',
      duration: value?.duration?.toString() ?? '',
      price: value?.price ?? '',
      code: value?.code ?? '',
      type: value?.type ?? '',
      activeState: value?.activeState ?? true,
      packageType: value?.packageType ?? '',
      creditCount: value?.creditCount?.toString() ?? '',
      creditExpirationDays: value?.creditExpirationDays?.toString() ?? '',
      companyId: value?.companyId ?? null,
      companyIds: companyIds,
      companySelection: companySelection,
      brokerId: value?.brokerId ?? null,
      brokerIds: brokerIds,
      brokerSelection: brokerSelection,
    });
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setAdvertisePackageStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  return (
    <>
      <Page title={'Advertise Package: List'}>
        <Container maxWidth={'xl'}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-end'
            spacing={{ xs: 0.5, sm: 1.5 }}
            my={1}
          >
            <Button
              variant='contained'
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={onOpen}
            >
              {'New Advertise Package'}
            </Button>
          </Stack>

          <Card>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" sx={{ pr: { sm: 3 } }}>
              <Box sx={{ flexGrow: 1, width: '100%' }}>
                <Searchbar handleSearch={handleSearch} />
              </Box>
              <FormControl sx={{ minWidth: 200, mx: { xs: 3, sm: 0 }, mb: { xs: 2.5, sm: 0 }, width: { xs: 'calc(100% - 48px)', sm: 'auto' } }}>
                <InputLabel id="user-type-select-label">User Type</InputLabel>
                <Select
                  labelId="user-type-select-label"
                  id="user-type-select"
                  value={userType}
                  label="User Type"
                  onChange={(e) => setUserType(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="individual">Individual</MenuItem>
                  <MenuItem value="broker">Broker</MenuItem>
                  <MenuItem value="company">Company</MenuItem>
                  <MenuItem value="individual_broker">Individual + Broker</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Scrollbar>
              <TableContainer
                sx={{ minWidth: 1000, position: 'relative', pt: 2 }}
              >
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Label EN'}</TableCell>
                      <TableCell align='center'>{'Label AR'}</TableCell>
                      <TableCell align='center'>{'Code'}</TableCell>
                      <TableCell align='center'>{'Price'}</TableCell>
                      <TableCell align='center'>{'Duration'}</TableCell>
                      <TableCell align='center'>{'Type'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((advertisePackage) => (
                        <TableRow key={advertisePackage.id}>
                          <TableCell align='center'>
                            {advertisePackage?.labelEn}
                          </TableCell>
                          <TableCell align='center'>
                            {advertisePackage?.labelAr}
                          </TableCell>
                          <TableCell align='center'>
                            {advertisePackage?.code}
                          </TableCell>
                          <TableCell align='center'>
                            {advertisePackage?.price}
                          </TableCell>
                          <TableCell align='center'>
                            {advertisePackage?.duration}
                          </TableCell>
                          <TableCell
                            align='center'
                            sx={{ textTransform: 'capitalize' }}
                          >
                            {advertisePackage?.type}
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={
                                advertisePackage.activeState
                                  ? 'success'
                                  : 'error'
                              }
                            >
                              {advertisePackage.activeState
                                ? 'Active'
                                : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(advertisePackage)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <AdvertisePackageFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
        </Container>
      </Page>
    </>
  );
};

export default Index;
